import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shuffle,
  Search,
  Filter,
  User,
  Phone,
  Mail,
  Building2,
  Calendar,
  Clock,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Loader2,
  History,
  Users
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from '../../hooks/use-toast';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { ScrollArea } from '../../components/ui/scroll-area';
import { usersAPI, leadBankAPI } from '../../services/api';
import { cn } from '../../lib/utils';

const LeadBankPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [unassignedLeads, setUnassignedLeads] = useState([]);
  const [stats, setStats] = useState({ total_unassigned: 0, by_source: {} });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [isShuffleOpen, setIsShuffleOpen] = useState(false);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [viewingLead, setViewingLead] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [usersRes, leadsRes, statsRes] = await Promise.all([
          usersAPI.list(),
          leadBankAPI.list({ page_size: 100 }),
          leadBankAPI.stats()
        ]);
        setUsers(usersRes.data || []);
        setUnassignedLeads(leadsRes.data?.leads || []);
        setStats(statsRes.data || { total_unassigned: 0, by_source: {} });
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const employees = users.filter(u => u.role === 'EMPLOYEE');

  const getEmployeeName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.name || 'Unknown';
  };

  const getAvailableAgents = () => {
    return employees;
  };

  const toggleLeadSelection = (id) => {
    setSelectedLeads(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleAllLeads = () => {
    if (selectedLeads.length === unassignedLeads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(unassignedLeads.map(l => l.id));
    }
  };

  const handleShuffle = async () => {
    if (selectedLeads.length === 0 || selectedAgents.length === 0) return;
    
    try {
      // Assign leads to selected agents in round-robin fashion
      for (let i = 0; i < selectedLeads.length; i++) {
        const agentIndex = i % selectedAgents.length;
        await leadBankAPI.assign(selectedLeads[i], selectedAgents[agentIndex]);
      }
      
      // Refresh the list
      const res = await leadBankAPI.list({ page_size: 100 });
      setUnassignedLeads(res.data?.leads || []);
      setIsShuffleOpen(false);
      setSelectedLeads([]);
      setSelectedAgents([]);
    } catch (error) {
      console.error('Failed to assign leads:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to assign leads',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const filteredLeads = unassignedLeads.filter(lead =>
    (lead.company_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (lead.contact_name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Lead Bank</h1>
          <p className="text-slate-500 dark:text-slate-400">Rejected leads available for re-assignment</p>
        </div>
        <Button 
          className="bg-slate-800 hover:bg-slate-900" 
          onClick={() => setIsShuffleOpen(true)}
          disabled={selectedLeads.length === 0}
        >
          <Shuffle className="w-4 h-4 mr-2" />
          Shuffle Selected ({selectedLeads.length})
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <Users className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Total in Bank</p>
                <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">{stats.total_unassigned}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">From Google Ads</p>
                <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                  {stats.by_source?.GOOGLE_ADS || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">From Website</p>
                <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                  {stats.by_source?.WEBSITE || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-slate-100 dark:bg-slate-700 rounded-lg">
                <Phone className="w-5 h-5 text-slate-600 dark:text-slate-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Other Sources</p>
                <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                  {Object.entries(stats.by_source || {}).filter(([k]) => !['GOOGLE_ADS', 'WEBSITE'].includes(k)).reduce((sum, [,v]) => sum + v, 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search leads..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white dark:bg-slate-800"
          />
        </div>
      </div>

      {/* Leads Table */}
      <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 dark:bg-slate-900">
                <TableHead className="w-[50px]">
                  <Checkbox
                    checked={selectedLeads.length === unassignedLeads.length && unassignedLeads.length > 0}
                    onCheckedChange={toggleAllLeads}
                  />
                </TableHead>
                <TableHead className="font-semibold">Company</TableHead>
                <TableHead className="font-semibold">Contact</TableHead>
                <TableHead className="font-semibold">Industry</TableHead>
                <TableHead className="font-semibold">Source</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Created</TableHead>
                <TableHead className="w-[100px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLeads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    No unassigned leads in the bank
                  </TableCell>
                </TableRow>
              ) : filteredLeads.map((lead) => (
                <TableRow 
                  key={lead.id} 
                  className={cn(
                    'hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer',
                    selectedLeads.includes(lead.id) && 'bg-blue-50 dark:bg-blue-900/20'
                  )}
                  onClick={() => setViewingLead(lead)}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedLeads.includes(lead.id)}
                      onCheckedChange={() => toggleLeadSelection(lead.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-slate-400" />
                      <span className="font-medium dark:text-slate-100">{lead.company_name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="dark:text-slate-300">
                      <p>{lead.contact_name}</p>
                      <p className="text-xs text-slate-500">{lead.contact_phone}</p>
                    </div>
                  </TableCell>
                  <TableCell className="dark:text-slate-300">{lead.industry || '-'}</TableCell>
                  <TableCell className="dark:text-slate-300">{lead.source_channel || 'Unknown'}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <Badge variant="outline" className={cn(
                        "dark:border-slate-600",
                        lead.is_rejected && "bg-red-100 text-red-700 border-red-200"
                      )}>
                        {lead.is_rejected ? 'Rejected' : (lead.current_status || 'NEW')}
                      </Badge>
                      {lead.is_rejected && lead.rejection_reason && (
                        <span className="text-xs text-red-500 truncate max-w-[150px]" title={lead.rejection_reason}>
                          {lead.rejection_reason}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="dark:text-slate-300">
                    {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm">
                      <History className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Shuffle Modal */}
      <Dialog open={isShuffleOpen} onOpenChange={setIsShuffleOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Shuffle Leads to Agents</DialogTitle>
            <DialogDescription>
              Select agents to receive the {selectedLeads.length} selected lead(s). 
              Agents who previously rejected these leads are excluded.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="mb-2 block">Available Agents</Label>
            <div className="space-y-2 max-h-60 overflow-y-auto p-2 border rounded">
              {getAvailableAgents().map(agent => (
                <div key={agent.id} className="flex items-center gap-2 p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded">
                  <Checkbox
                    checked={selectedAgents.includes(agent.id)}
                    onCheckedChange={(checked) => {
                      setSelectedAgents(checked
                        ? [...selectedAgents, agent.id]
                        : selectedAgents.filter(id => id !== agent.id)
                      );
                    }}
                  />
                  <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-xs font-medium">
                    {agent.avatar}
                  </div>
                  <span className="dark:text-slate-300">{agent.name}</span>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsShuffleOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleShuffle} 
              disabled={selectedAgents.length === 0}
              className="bg-slate-800 hover:bg-slate-900"
            >
              <Shuffle className="w-4 h-4 mr-2" />
              Shuffle to {selectedAgents.length} Agent(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead Profile Modal */}
      <Dialog open={!!viewingLead} onOpenChange={() => setViewingLead(null)}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{viewingLead?.company_name}</DialogTitle>
            <DialogDescription>Complete lead history and profile</DialogDescription>
          </DialogHeader>
          {viewingLead && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <div>
                  <Label className="text-slate-500">Contact Name</Label>
                  <p className="font-medium dark:text-slate-100">{viewingLead.contact_name}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Phone</Label>
                  <p className="font-medium dark:text-slate-100">{viewingLead.contact_phone}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Email</Label>
                  <p className="font-medium dark:text-slate-100">{viewingLead.contact_email}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Industry</Label>
                  <p className="font-medium dark:text-slate-100">{viewingLead.industry}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Source</Label>
                  <p className="font-medium dark:text-slate-100">{viewingLead.source}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Rejected By</Label>
                  <p className="font-medium dark:text-slate-100">{getEmployeeName(viewingLead.rejected_by)}</p>
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Activity History</Label>
                <ScrollArea className="h-[200px] border rounded-lg p-3">
                  <div className="space-y-3">
                    {viewingLead.history.map((item, idx) => (
                      <div key={idx} className="flex gap-3">
                        <div className={cn(
                          'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                          item.action === 'REJECTED' ? 'bg-red-100 text-red-600' :
                          item.action === 'CREATED' ? 'bg-blue-100 text-blue-600' :
                          'bg-slate-100 text-slate-600'
                        )}>
                          {item.action === 'REJECTED' ? <XCircle className="w-4 h-4" /> :
                           item.action === 'CREATED' ? <CheckCircle2 className="w-4 h-4" /> :
                           <Clock className="w-4 h-4" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm dark:text-slate-100">{item.action}</span>
                            <span className="text-xs text-slate-500">by {item.by}</span>
                            <span className="text-xs text-slate-400">{item.at}</span>
                          </div>
                          <p className="text-sm text-slate-600 dark:text-slate-400">{item.note}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingLead(null)}>Close</Button>
            <Button className="bg-slate-800 hover:bg-slate-900">
              <Shuffle className="w-4 h-4 mr-2" />
              Reassign Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadBankPage;
